import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Link, useLocation, useNavigate} from "react-router-dom";
import Modal from "../components/Modal";
import {getImages} from "../components/PropertyCard";
import {useAuth} from "../context/AuthContext";
import {getProperties, submitPublicInquiry} from "../services/api";
import {encodeId} from "../utils/idEncoder";

const DISTRICTS = [
    "Srinagar",
    "Jammu",
    "Anantnag",
    "Baramulla",
    "Budgam",
    "Bandipora",
    "Ganderbal",
    "Pulwama",
    "Shopian",
    "Kupwara",
    "Kulgam",
    "Kathua",
    "Rajouri",
    "Poonch",
    "Udhampur",
    "Reasi",
    "Doda",
    "Ramban",
    "Kishtwar",
    "Samba"
];

const INITIAL_SEARCH = {
    type: "",
    district: "",
    min_price: "",
    max_price: ""
};

const INITIAL_ENQUIRY = {
    name: "",
    phone: "",
    budget: ""
};

function normalizeType(value) {
    const raw = String(value || "house").toLowerCase();
    if (raw === "flat" || raw === "apartment") {
        return "house";
    }
    if (raw === "land" || raw === "shop" || raw === "house") {
        return raw;
    }
    return "house";
}

function normalizeListingType(value) {
    return String(value || "sale").toLowerCase() === "rent" ? "rent" : "sale";
}

function toNumber(value) {
    if (value === "" || value === null || value === undefined) {
        return null;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
}

function normalizeProperty(data) {
    const areaValue = data.area_value ?? data.Area ?? data.LandMarla ?? null;
    const areaUnit = data.area_unit || (data.LandKanal ? "Kanal" : data.LandMarla ? "Marla" : null);

    return {
        id: data.id,
        title: data.title || data.TagLine || "Property",
        type: normalizeType(data.type || data.PropertyType),
        listingType: normalizeListingType(data.listing_type || data.ListingType),
        city: data.city || data.City || "Jammu & Kashmir",
        district: data.district || data.District || data.city || data.City || "Jammu & Kashmir",
        locationLabel: data.location_label || data.Location || "Jammu & Kashmir",
        price: Number(data.price ?? data.Price ?? 0),
        minPrice: toNumber(data.min_price),
        maxPrice: toNumber(data.max_price),
        bedrooms: Number(data.bedrooms ?? data.NumberOfRooms ?? 0),
        bathrooms: Number(data.bathrooms ?? data.NumberOfWashrooms ?? 0),
        commonWashrooms: Number(data.common_washrooms ?? 0),
        attachedWashrooms: Number(data.attached_washrooms ?? 0),
        areaSqft: Number(data.area_sqft ?? 0),
        areaValue: toNumber(areaValue),
        areaUnit: areaUnit || "SqFt",
        propertyImages: Array.isArray(data.property_images) ? data.property_images : (data.property_images ? [data.property_images] : []),
        shopUnits: Number(data.shop_units ?? data.NumberOfShops ?? 0),
        contactName: data.contact_name || null,
        contactPhone: data.contact_phone || data.Contact || null,
        brokerName: data.broker_name || null,
        latitude: data.latitude ?? null,
        longitude: data.longitude ?? null,
        isVerified: data.is_verified || false,
    };
}

function formatPrice(value, listingType) {
    const amount = Number(value || 0);
    if (listingType === "rent") {
        return `\u20b9 ${amount.toLocaleString("en-IN")}/mo`;
    }

    if (amount >= 10000000) {
        return `\u20b9 ${(amount / 10000000).toFixed(amount >= 50000000 ? 0 : 1)} Crore`;
    }
    if (amount >= 100000) {
        return `\u20b9 ${(amount / 100000).toFixed(amount >= 1000000 ? 0 : 1)} Lakh`;
    }
    return `\u20b9 ${amount.toLocaleString("en-IN")}`;
}

function areaLabel(property) {
    if (property.areaValue && property.areaUnit) {
        return `${property.areaValue} ${property.areaUnit}`;
    }
    if (property.areaSqft) {
        return `${property.areaSqft} sq ft`;
    }
    return "-";
}

function typeIcon(propertyType) {
    if (propertyType === "land") {
        return "🌿";
    }
    if (propertyType === "shop") {
        return "🏪";
    }
    return "🏠";
}

export default function Home() {
    const navigate = useNavigate();
    const location = useLocation();
    const listingsSectionRef = useRef(null);
    const {isAuthenticated} = useAuth();

    const [listingFilter, setListingFilter] = useState("all");
    const [modalType, setModalType] = useState(null);
    const [searchMode, setSearchMode] = useState("sale");

    const [allProperties, setAllProperties] = useState([]);
    const [searchForm, setSearchForm] = useState(INITIAL_SEARCH);
    const [appliedSearch, setAppliedSearch] = useState(null);
    const [revealedMap, setRevealedMap] = useState({});

    const [enquiryTarget, setEnquiryTarget] = useState(null);
    const [enquiryForm, setEnquiryForm] = useState(INITIAL_ENQUIRY);

    const [loading, setLoading] = useState(true);
    const [globalNotice, setGlobalNotice] = useState({type: "", text: ""});
    const [submitting, setSubmitting] = useState(false);
    const [shouldScrollToListings, setShouldScrollToListings] = useState(false);

    const loadVisibleProperties = useCallback(async () => {
        const data = await getProperties();
        const visibleProperties = Array.isArray(data)
            ? data.filter((item) => item?.status === "published" && item?.is_published === true)
            : [];
        setAllProperties(visibleProperties.map(normalizeProperty));
    }, []);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                await loadVisibleProperties();
            } catch {
                setAllProperties([]);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [loadVisibleProperties]);

    const filteredProperties = useMemo(() => {
        return allProperties.filter((item) => {
            const byListingType = listingFilter === "all" || item.listingType === listingFilter;
            if (!byListingType) {
                return false;
            }

            if (!appliedSearch) {
                return true;
            }

            const bySearchMode = !appliedSearch.listing_type || item.listingType === appliedSearch.listing_type;
            const byType = !appliedSearch.type || item.type === appliedSearch.type;
            const byDistrict =
                !appliedSearch.district ||
                item.district.toLowerCase().includes(appliedSearch.district.toLowerCase()) ||
                item.city.toLowerCase().includes(appliedSearch.district.toLowerCase());
            const byMin = !appliedSearch.min_price || item.price >= Number(appliedSearch.min_price);
            const byMax = !appliedSearch.max_price || item.price <= Number(appliedSearch.max_price);

            return bySearchMode && byType && byDistrict && byMin && byMax;
        });
    }, [allProperties, appliedSearch, listingFilter]);

    const topStats = useMemo(() => {
        const districtCount = new Set(allProperties.map((item) => item.district).filter(Boolean)).size;
        return [
            {value: districtCount || "20+", label: "Districts", icon: "\uD83D\uDDFA\uFE0F"},
            {value: allProperties.length || "100+", label: "Active Listings", icon: "\uD83C\uDFE0"},
            {value: "500+", label: "Happy Clients", icon: "\u2B50"},
            {value: "100%", label: "Verified", icon: "\u2714"}
        ];
    }, [allProperties]);

    useEffect(() => {
        window.dispatchEvent(new CustomEvent("fy-stats-update", {detail: {stats: topStats}}));
    }, [topStats]);

    const closeModal = () => {
        setModalType(null);
        setSubmitting(false);
    };

    const openSearch = useCallback((mode) => {
        setSearchMode(mode === "rent" ? "rent" : "sale");
        setSearchForm(INITIAL_SEARCH);
        setModalType("search");
    }, []);

    const startPostingFlow = useCallback((mode) => {
        const listingType = mode === "rent" ? "rent" : "sale";
        const prompt =
            listingType === "rent"
                ? "Login to continue posting your rental property."
                : "Login to continue posting your property.";

        if (!isAuthenticated) {
            navigate("/login", {
                state: {
                    from: location.pathname,
                    openPostProperty: true,
                    listingType,
                    postPropertySource: "site",
                    prompt
                }
            });
            return;
        }

        window.dispatchEvent(new CustomEvent("fy-open-post-property", {detail: {listingType, source: "site"}}));
    }, [isAuthenticated, location.pathname, navigate]);

    const openSellFlow = useCallback((mode) => {
        startPostingFlow(mode);
    }, [startPostingFlow]);

    const openRentFlow = useCallback(() => {
        setModalType("rent-choice");
    }, []);

    useEffect(() => {
        const actionFromRoute = location.state?.marketAction;
        if (!actionFromRoute) {
            return;
        }

        if (actionFromRoute === "buy") {
            openSearch("sale");
        } else if (actionFromRoute === "sell") {
            openSellFlow("sale");
        } else if (actionFromRoute === "rent") {
            openRentFlow();
        }

        navigate(location.pathname, {replace: true, state: null});
    }, [location.pathname, location.state, navigate, openRentFlow, openSearch, openSellFlow]);

    useEffect(() => {
        const onMarketAction = (event) => {
            const action = event?.detail?.action;
            if (action === "buy") {
                openSearch("sale");
            } else if (action === "sell") {
                openSellFlow("sale");
            } else if (action === "rent") {
                openRentFlow();
            }
        };

        window.addEventListener("fy-market-action", onMarketAction);
        return () => window.removeEventListener("fy-market-action", onMarketAction);
    }, [openRentFlow, openSearch, openSellFlow]);

    useEffect(() => {
        if (!shouldScrollToListings) {
            return;
        }

        const frameId = window.requestAnimationFrame(() => {
            listingsSectionRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start"
            });
            setShouldScrollToListings(false);
        });

        return () => window.cancelAnimationFrame(frameId);
    }, [filteredProperties.length, shouldScrollToListings]);

    const clearAllListingFilters = () => {
        setAppliedSearch(null);
        setSearchForm(INITIAL_SEARCH);
        setListingFilter("all");
        setGlobalNotice({type: "", text: ""});
    };

    const onRevealContactAndMap = (propertyId) => {
        if (!isAuthenticated) {
            navigate("/login", {
                state: {
                    from: location.pathname,
                    prompt: "Login to reveal contact and map details for listings."
                }
            });
            return;
        }

        setRevealedMap((prev) => ({...prev, [propertyId]: !prev[propertyId]}));
    };

    const onSearchSubmit = () => {
        const payload = {
            listing_type: searchMode,
            type: searchForm.type || "",
            district: searchForm.district || "",
            min_price: searchForm.min_price || "",
            max_price: searchForm.max_price || ""
        };

        setAppliedSearch(payload);
        setListingFilter(searchMode);
        setGlobalNotice({
            type: "success",
            text: `Showing ${searchMode === "rent" ? "rent" : "buy"} results${payload.district ? ` for ${payload.district}` : ""}.`
        });
        setShouldScrollToListings(true);
        closeModal();
    };

    const onSubmitEnquiry = async (event) => {
        event.preventDefault();
        if (!enquiryTarget) {
            return;
        }

        if (!enquiryForm.name.trim() || !enquiryForm.phone.trim()) {
            setGlobalNotice({type: "error", text: "Name and phone number are required for enquiry."});
            return;
        }

        try {
            setSubmitting(true);
            await submitPublicInquiry({
                property_id: Number(enquiryTarget.id),
                name: enquiryForm.name.trim(),
                phone: enquiryForm.phone.trim(),
                budget: toNumber(enquiryForm.budget),
                message: `Enquiry for ${enquiryTarget.title}`
            });

            setGlobalNotice({type: "success", text: "Enquiry sent. Owner/admin will contact you shortly."});
            setEnquiryForm(INITIAL_ENQUIRY);
            closeModal();
        } catch (requestError) {
            const detail = requestError?.response?.data?.detail || "Failed to send enquiry.";
            setGlobalNotice({type: "error", text: String(detail)});
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fy-page">
            <div className="fy-shell">
                <section className="fy-hero">
                    <div className="fy-hero-inner">
                        <p className="fy-kicker">JAMMU &amp; KASHMIR&apos;S PREMIER PROPERTY PLATFORM</p>
                        <h1>
                            Find Your <span>Dream</span>
                            <br/>
                            Property in J&amp;K
                        </h1>
                        <p className="fy-hero-copy">
                            Buy, sell, or rent land, houses and shops across all 20 districts of Jammu &amp; Kashmir
                            with
                            verified listings and direct owner contact.
                        </p>

                        <div className="fy-hero-actions">
                            <button type="button" className="fy-btn fy-btn-primary" onClick={() => openSearch("sale")}>
                                🔍 Search to Buy
                            </button>
                            <button type="button" className="fy-btn fy-btn-outline"
                                    onClick={() => openSellFlow("sale")}>
                                🏷 List Your Property
                            </button>
                            <button type="button" className="fy-btn fy-btn-outline" onClick={openRentFlow}>
                                🔑 Rent Options
                            </button>
                        </div>
                    </div>
                </section>


                <section ref={listingsSectionRef} className="fy-listings">
                    <div className="fy-listings-inner">
                        <div className="fy-listings-head">
                            <div>
                                <h2>Latest Listings</h2>
                                <p>{filteredProperties.length} properties found</p>
                            </div>
                            <div className="fy-listing-tabs">
                                <button
                                    type="button"
                                    className={`fy-filter-tab ${listingFilter === "all" ? "active" : ""}`}
                                    onClick={clearAllListingFilters}
                                >
                                    All
                                </button>
                                <button
                                    type="button"
                                    className={`fy-filter-tab ${listingFilter === "sale" ? "active" : ""}`}
                                    onClick={() => setListingFilter("sale")}
                                >
                                    Sale
                                </button>
                                <button
                                    type="button"
                                    className={`fy-filter-tab ${listingFilter === "rent" ? "active" : ""}`}
                                    onClick={() => setListingFilter("rent")}
                                >
                                    Rent
                                </button>
                            </div>
                        </div>

                        {globalNotice.text ? (
                            <div className={`fy-notice ${globalNotice.type === "error" ? "error" : "success"}`}>
                                <span>{globalNotice.text}</span>
                                {appliedSearch ? (
                                    <button
                                        type="button"
                                        className="fy-notice-action"
                                        onClick={clearAllListingFilters}
                                    >
                                        Clear Filters
                                    </button>
                                ) : null}
                            </div>
                        ) : null}

                        {loading ? <div className="fy-empty">Loading listings...</div> : null}
                        {!loading && filteredProperties.length === 0 ? (
                            <div className="fy-empty">No listings match current filters.</div>
                        ) : null}

                        <div className="fy-card-grid">
                            {filteredProperties.map((property) => {
                                const showReveal = Boolean(revealedMap[property.id]);

                                return (
                                    <article key={property.id} className="fy-card">
                                        <Link to={`/property/${encodeId(property.id)}`} className="fy-card-media-link"
                                              aria-label={`View ${property.title}`}>
                                            <div className="fy-card-media">
                                                <img
                                                    src={getImages(property.propertyImages[0] ?? null, property.type)}
                                                    alt={property.title}
                                                    className="fy-card-img"
                                                    loading="lazy"
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = getImages(null, property.type);
                                                    }}
                                                />
                                                <span
                                                    className={`fy-card-badge ${property.listingType}`}>{property.listingType === "rent" ? "Rent" : "Sale"}</span>
                                            </div>
                                        </Link>

                                        <div className="fy-card-body">
                                            <div className="fy-card-row">
                                                <h3 className="fy-card-title" title={property.title}>
                                                    {property.title || (property.type.charAt(0).toUpperCase() + property.type.slice(1))}
                                                </h3>
                                               <strong>
                                                  {property.minPrice != null && property.maxPrice != null
                                                    ? `${formatPrice(property.minPrice, property.listingType)} – ${formatPrice(property.maxPrice, property.listingType)}`
                                                    : property.minPrice != null
                                                      ? `From ${formatPrice(property.minPrice, property.listingType)}`
                                                      : property.maxPrice != null
                                                        ? `Up to ${formatPrice(property.maxPrice, property.listingType)}`
                                                        : formatPrice(property.price, property.listingType)}
                                                </strong>
                                            </div>
                                            <p className="fy-card-location">📍 {property.locationLabel}</p>
                                            {property.isVerified ? (
                                                <span className="fy-card-meta">✅ Verified</span>
                                            ) : null}


                                            {property.type === "house" ? (
                                                <p className="fy-card-meta">🛏 {property.bedrooms} Rooms</p>
                                            ) : (
                                                <p className="fy-card-meta">{areaLabel(property)}</p>
                                            )}
                                            {property.brokerName ? (
                                                <p className="fy-card-meta">🧑‍💼 {property.brokerName}</p>
                                            ) : null}


                                            {
                                                showReveal ? (
                                                    <div className="fy-reveal-data">
                                                        <div>📞 {property.contactName || "Contact shared after login"} • {property.contactPhone || "Phone unavailable"}</div>
                                                        <div>
                                                            {property.latitude != null && property.longitude != null ? (
                                                                <a
                                                                    href={"https://maps.google.com/?q=" + property.latitude + "," + property.longitude}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    style={{
                                                                        color: "inherit",
                                                                        textDecoration: "underline"
                                                                    }}
                                                                >
                                                                    🗺 View on Google Maps
                                                                </a>
                                                            ) : (
                                                                "🗺 " + property.district + ", J&K"
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : null
                                            }


                                            <button
                                                type="button"
                                                className="fy-card-btn fy-card-btn-ghost"
                                                onClick={() => onRevealContactAndMap(property.id)}
                                            >
                                                🔒 Reveal Contact &amp; Map
                                            </button>

                                            <button
                                                type="button"
                                                className="fy-card-btn fy-card-btn-main"
                                                onClick={() => {
                                                    setEnquiryTarget(property);
                                                    setModalType("enquiry");
                                                }}
                                            >
                                                Enquire Now
                                            </button>
                                        </div>
                                    </article>
                                )
                                    ;
                            })}
                        </div>
                    </div>
                </section>

            </div>

            {modalType ? (
                <Modal onClose={closeModal}>
                    {modalType === "search" ? (
                        <>
                            <div className="fy-modal-head">
                                <h3>🔍 Find Your Property</h3>
                            </div>

                            <label className="fy-label">Property Type</label>
                            <select
                                value={searchForm.type}
                                onChange={(event) => setSearchForm((prev) => ({...prev, type: event.target.value}))}
                            >
                                <option value="">All Types</option>
                                <option value="house">House</option>
                                <option value="land">Land</option>
                                <option value="shop">Shop</option>
                            </select>

                            <label className="fy-label">District</label>
                            <select
                                value={searchForm.district}
                                onChange={(event) => setSearchForm((prev) => ({...prev, district: event.target.value}))}
                            >
                                <option value="">All Districts</option>
                                {DISTRICTS.map((district) => (
                                    <option key={district} value={district}>{district}</option>
                                ))}
                            </select>

                            <div className="fy-grid-2">
                                <div>
                                    <label className="fy-label">Min Budget (₹)</label>
                                    <input
                                        type="number"
                                        placeholder="e.g. 500000"
                                        value={searchForm.min_price}
                                        onChange={(event) => setSearchForm((prev) => ({
                                            ...prev,
                                            min_price: event.target.value
                                        }))}
                                    />
                                </div>
                                <div>
                                    <label className="fy-label">Max Budget (₹)</label>
                                    <input
                                        type="number"
                                        placeholder="e.g. 5000000"
                                        value={searchForm.max_price}
                                        onChange={(event) => setSearchForm((prev) => ({
                                            ...prev,
                                            max_price: event.target.value
                                        }))}
                                    />
                                </div>
                            </div>

                            <button type="button" className="fy-submit" onClick={onSearchSubmit}>
                                Search Properties →
                            </button>
                        </>
                    ) : null}

                    {modalType === "rent-choice" ? (
                        <>
                            <div className="fy-modal-head">
                                <h3>🔑 Rent</h3>
                            </div>
                            <p className="fy-subcopy">What would you like to do?</p>

                            <button
                                type="button"
                                className="fy-action-card"
                                onClick={() => {
                                    startPostingFlow("rent");
                                }}
                            >
                                <strong>🏷 List Property for Rent</strong>
                                <span>Have a property? List it and find tenants.</span>
                            </button>

                            <button
                                type="button"
                                className="fy-action-card"
                                onClick={() => {
                                    openSearch("rent");
                                }}
                            >
                                <strong>🔎 Find Property for Rent</strong>
                                <span>Looking to rent? Search available properties.</span>
                            </button>
                        </>
                    ) : null}

                    {modalType === "enquiry" && enquiryTarget ? (
                        <form onSubmit={onSubmitEnquiry}>
                            <div className="fy-modal-head">
                                <h3>Enquire: {enquiryTarget.title || (enquiryTarget.type.charAt(0).toUpperCase() + enquiryTarget.type.slice(1))} in {enquiryTarget.district}</h3>
                            </div>

                            <div className="fy-property-chip">
                                <strong>{enquiryTarget.title || (enquiryTarget.type.charAt(0).toUpperCase() + enquiryTarget.type.slice(1))}</strong> · {enquiryTarget.district} · <strong>{enquiryTarget.title || (enquiryTarget.type.charAt(0).toUpperCase() + enquiryTarget.type.slice(1))}</strong> · {enquiryTarget.district} · {enquiryTarget.minPrice != null && enquiryTarget.maxPrice != null
  ? `${formatPrice(enquiryTarget.minPrice, enquiryTarget.listingType)} – ${formatPrice(enquiryTarget.maxPrice, enquiryTarget.listingType)}`
  : formatPrice(enquiryTarget.price, enquiryTarget.listingType)} · {areaLabel(enquiryTarget)}·{areaLabel(enquiryTarget)}
                            </div>

                            <label className="fy-label">Your Name *</label>
                            <input
                                placeholder="Full name"
                                value={enquiryForm.name}
                                onChange={(event) => setEnquiryForm((prev) => ({...prev, name: event.target.value}))}
                            />

                            <label className="fy-label">Phone Number *</label>
                            <input
                                placeholder="Your contact number"
                                value={enquiryForm.phone}
                                onChange={(event) => setEnquiryForm((prev) => ({...prev, phone: event.target.value}))}
                            />

                            <label className="fy-label">Your Budget (Optional)</label>
                            <input
                                placeholder="e.g. 50 Lakh"
                                value={enquiryForm.budget}
                                onChange={(event) => setEnquiryForm((prev) => ({...prev, budget: event.target.value}))}
                            />

                            <button type="submit" className="fy-submit" disabled={submitting}>
                                {submitting ? "Sending..." : "Send Enquiry →"}
                            </button>

                            <p className="fy-note">
                                Your details will be shared with the property owner/admin for response.
                            </p>
                        </form>
                    ) : null}
                </Modal>
            ) : null}
        </div>
    );
}
